import { supabase } from '@/lib/supabaseClient';

interface RetryOptions {
  maxAttempts?: number;
  baseDelayMs?: number;
  onRetry?: (attempt: number, error: unknown) => void;
}

const DEFAULT_OPTIONS: Required<Omit<RetryOptions, 'onRetry'>> = {
  maxAttempts: 3,
  baseDelayMs: 1000,
};

function getDelayMs(attempt: number, baseDelayMs: number): number {
  return baseDelayMs * Math.pow(2, attempt - 1);
}

function isRetryableError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  const err = error as { status?: number; code?: string; message?: string };

  // 401 Unauthorized - token may have expired, try refreshing
  if (err.status === 401) return true;

  // 429 Too Many Requests
  if (err.status === 429) return true;

  // 500+ Server errors
  if (err.status && err.status >= 500) return true;

  // Network errors
  if (err.message?.includes('fetch') || err.message?.includes('network') || err.message?.includes('Failed to fetch')) {
    return true;
  }

  return false;
}

async function handleAuthRefresh(): Promise<boolean> {
  try {
    const { error } = await supabase.auth.refreshSession();
    return !error;
  } catch {
    return false;
  }
}

export async function withRetry<T>(
  fn: () => PromiseLike<T> | Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const { maxAttempts, baseDelayMs } = { ...DEFAULT_OPTIONS, ...options };

  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt === maxAttempts || !isRetryableError(error)) {
        throw error;
      }

      const err = error as { status?: number };

      // Handle 401 by refreshing auth token
      if (err.status === 401) {
        const refreshed = await handleAuthRefresh();
        if (!refreshed) throw error;
      }

      options.onRetry?.(attempt, error);

      // For 429, use longer delay
      const delay = err.status === 429
        ? getDelayMs(attempt, baseDelayMs * 4)
        : getDelayMs(attempt, baseDelayMs);

      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}
